export enum EffectType {
	text = 'text',
	deleteEffect = 'deleteEffect',
	dr = 'dr',
	vun = 'vun',
	stat = 'stat',
	sense = 'sense',
	skill = 'skill',
	ac = 'ac',
	toHit = 'toHit',
	toDamage = 'toDamage',
	hpMax = 'hpMax',
	damage = 'damage',
	treatedDamage = 'treatedDamage',
	rankRequired = 'rankRequired',
	minRequired = 'minRequired',
	maxRequired = 'maxRequired',
	numRequired = 'numRequired',
	boost = 'boost',
	boon = 'boon',
	tieredBoon = 'tieredBoon',
	lookup = 'lookup',
	bonusSP = 'bonusSP',
	bonusAP = 'bonusAP',
	bonusStat = 'bonusStat',
	bonusFeat = 'bonusFeat',
	bonusKnack = 'bonusKnack',
	bonusHP = 'bonusHP',
	bonusWound = 'bonusWound',
	creatureMod = 'creatureMod',
}

// creatureMod: percentage-based creature stat modifier.
//   lookup = stat to modify (health, mass, might, speed, energy, tr)
//   adjustment = percentage (e.g. -25 for -25%, 25 for +25%)
//   value = "" (unused)
//   Stacks additively: Juvenile(-25%) + another debuff(-10%) = -35% total.

// Effect Type Usage:
// - rankRequired: value = minimum character rank required (e.g., "1")
// - minRequired: value = minimum number of selections required (e.g., "2")
// - maxRequired: value = maximum number of selections allowed (e.g., "3")
// - numRequired: value = exact number of selections required (e.g., "1")
// - boost: flat +5% per stack. Knack retakes stack. Helping mechanic (2 Actions = +5%).
// - boon: RankedBoon — legacy skill boon, auto-scales via dice chain per rank
//     Dice chain: d4→d6→d8→d10→d12→d20→d20+d4→d20+d6→d20+d8→d20+d10
//     R1=d4, R2=d6, R3=d8, R4=d10, R5=d12, R6=d20, R7=d20+d4 ... R10=d20+d10
// - tieredBoon: knack boon, auto-scales via dice per tier
//     T1(R1-3)=d4, T2(R4-6)=d6, T3(R7-10)=d8
// - lookup: value = property ID to link to (for skill boost properties)
//
// Bonus effect types (bonusSP, bonusAP, bonusStat, bonusFeat, bonusKnack, bonusHP, bonusWound):
//   adjustment = bonus amount, value = target ID (e.g., skill ID) or "" for global
//   dice field encodes frequency:
//     "once" = one-off grant (apply once when taken)
//     "1"    = every rank (flat adjustment each rank)
//     "2"    = every 2 ranks
//     "3"    = every 3 ranks
//     "tier" = once per tier (T1: 0-3, T2: 4-6, T3: 7-10)
//     "rank" = scales with rank (bonus = adjustment × currentRank: +0 at R0, +1 at R1, +2 at R2...)

// Damage lookup type: when EffectType.damage, lookup should be 'damage' and
// value should be one of DR_TYPES (the damage type). Adjustment = the hit count.
//   Example: type=damage, lookup=damage, value=pierce, adjustment=2  (2 Pierce hits)
//   Damage and DR share one value vocabulary (DR_TYPES). Resolve via
//   damageTypeOf / drTypeOf in foundary_vtt/module/utils/effect-types.mjs.

// DR lookup type: when EffectType.dr, lookup should be 'dr' and
// value should be one of DR_TYPES. Adjustment = the DR magnitude.
//   Example: type=dr, lookup=dr, value=pierce, adjustment=3
//   Meaning: "DR section, Pierce, reduce by 3"
export const DR_TYPES = [
	'blunt', 'pierce', 'slash', 'thermal', 'laser',
	'radiation', 'electrical', 'chemical', 'grav'
] as const;
export type DRType = typeof DR_TYPES[number];

export interface Effect {
	id: string;
	description: string;
	type: EffectType;
	lookup: string;
	value: string;
	adjustment: number;
	dice: string;
	/** UID of the owner; transfers to SYSTEM_OWNER_UID when the parent property
	 *  is made Public or its author deletes their profile. Optional for
	 *  backward compatibility (absent = system-owned / admin-managed). */
	ownerId?: string;
}

export const getDefaultEffect = (): Effect => {
	return {
		id: '-1',
		type: EffectType.text,
		description: '',
		lookup: '',
		value: '',
		adjustment: 0,
		dice: ''
	}
}

/**
 * Generate a unique effect ID from its key properties
 * Pattern: <type>-<lookup>-<value>-<adjustment>
 *
 * @example
 * generateEffectId('boost', 'skill', 'athletics', 2)
 * // Returns: "boost-skill-athletics-2"
 */
export function generateEffectId(
	type: EffectType,
	lookup: string,
	value: string,
	adjustment: number
): string {
	// Sanitize strings for ID: lowercase, replace spaces/special chars with hyphens
	const sanitize = (str: string) => str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

	return `${type}-${sanitize(lookup)}-${sanitize(value)}-${adjustment}`;
}